import React, { useEffect, useState } from 'react';
import OrderPicker from '../costing/OrderPicker';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ItemService } from '../../services/item.service';
import { RequisitionService } from '../../services/requisition.service';
import { Item } from '../../interfaces/item.interface';
import {
    RequisitionCreateDTO,
    RequisitionDetail
} from '../../interfaces/requisition.interface';
import {AuthService} from "../../services/auth.service";
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';

const RequisitionCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission, isAdmin, isSystemAdmin, permissionsReady } = useAuth();
    const canRequisition = isAdmin || isSystemAdmin
        || hasPermission(PERMISSIONS.MENU_REQUISITIONS) || hasPermission(PERMISSIONS.REQUISITION_CREATE);
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [remarks, setRemarks] = useState('');
    const [buyerOrderId, setBuyerOrderId] = useState('');
    const [details, setDetails] = useState<RequisitionDetail[]>([{ itemId: '', requiredQty: 0 }]);

    useEffect(() => {
        if (permissionsReady && !canRequisition) {
            setLoading(false);
            return;
        }
        if (permissionsReady) fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsReady, canRequisition]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(0, 100, '', 'STOCK'); // FW-2A
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (index: number, itemId: string) => {
        const newDetails = [...details];
        newDetails[index] = {
            ...newDetails[index],
            itemId
        };
        setDetails(newDetails);
    };

    const handleQuantityChange = (index: number, event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const value = Number(event.target.value);
        if (value >= 0) {
            const newDetails = [...details];
            newDetails[index] = {
                ...newDetails[index],
                requiredQty: value
            };
            setDetails(newDetails);
        }
    };

    const addItem = () => {
        setDetails([...details, { itemId: '', requiredQty: 0 }]);
    };

    const removeItem = (index: number) => {
        const newDetails = details.filter((_, i) => i !== index);
        setDetails(newDetails);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (details.some(detail => !detail.itemId || detail.requiredQty <= 0)) {
            toast.showError('Please fill in all fields with valid values');
            return;
        }

        try {
            const requisition: RequisitionCreateDTO = {
                institutionId: AuthService.getSelectedInstitution()!!,
                ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
                ...(buyerOrderId ? { buyerOrderId } : {}),
                details: details
            };

            await RequisitionService.createRequisition(requisition);
            toast.showSuccess('Requisition submitted');
            navigate('/my-requisitions');
        } catch (error) {
            console.error('Error creating requisition:', error);
            toast.showApiError(error, 'Failed to create requisition');
        }
    };

    if (!permissionsReady || loading) {
        return <div>Loading...</div>;
    }

    if (!canRequisition) {
        return (
            <div style={{ padding: '20px' }}>
                <Typography variant="h4" gutterBottom>Create Requisition</Typography>
                <Alert severity="info" sx={{ maxWidth: 600, mb: 2 }}>
                    You do not have access to raise store requisitions. Please ask your administrator if you need it.
                </Alert>
                <Button data-help="store.reqCreate.backHome" variant="outlined" onClick={() => navigate('/')}>Back to home</Button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.reqCreate.page">
            <Typography variant="h4" gutterBottom>
                Create Requisition
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800 }}>
                {details.map((detail, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }} data-help-section="store.reqCreate.items">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Item {index + 1}</Typography>
                            {details.length > 1 && (
                                <Button
                                    data-help="store.reqCreate.removeItem" startIcon={<DeleteIcon />}
                                    color="error"
                                    onClick={() => removeItem(index)}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>

                        <SearchableSelect<Item>
                            options={items}
                            value={detail.itemId}
                            onChange={id => handleItemChange(index, id)}
                            getOptionLabel={item => `${item.itemName} - Category: ${item.itemCategory?.fullName || item.itemCategory?.itemCategoryName}`}
                            data-help="store.reqCreate.field.item" label="Select Item"
                            required
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            data-help="store.reqCreate.field.qty" label="Required Quantity"
                            type="number"
                            value={detail.requiredQty}
                            onChange={(e) => handleQuantityChange(index, e)}
                            required
                            InputProps={{
                                inputProps: { min: 1 }
                            }}
                        />
                    </Box>
                ))}

                <OrderPicker data-help="store.reqCreate.field.order" value={buyerOrderId} onChange={setBuyerOrderId} helperText="Costs the issued material to this order's WIP" sx={{ mb: 2 }} />

                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    data-help="store.reqCreate.field.remarks" label="Purpose / remarks (optional)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${remarks.length}/500`}
                    sx={{ mb: 3 }}
                />

                <Button
                    data-help="store.reqCreate.addItem"
                    startIcon={<AddIcon />}
                    onClick={addItem}
                    sx={{ mb: 2 }}
                >
                    Add Another Item
                </Button>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        data-help="store.reqCreate.cancel" onClick={() => navigate('/my-requisitions')}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-help="store.reqCreate.submit"
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        Create Requisition
                    </Button>
                </Box>
            </Box>
        </div>
    );
};

export default RequisitionCreate;
